<script setup>
import { computed } from 'vue';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { messageStamp } from 'shared/helpers/timeHelper';

const { contentAttributes, createdAt } = useMessageContext();

const readableTime = computed(() =>
  messageStamp(createdAt.value, 'MMM d yyyy, h:mm a')
);

const normalizedAttributes = computed(() => {
  const attrs = contentAttributes?.value || contentAttributes || {};
  return {
    preText: attrs.pre_text || attrs.preText || '',
    items: (attrs.items || []).map(item => ({
      ...item,
      // Normalize item keys if needed, though getOrderId handles it
    })),
  };
});
const { t } = useI18n();

const PLACEHOLDER_IMAGE =
  'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2248%22 height=%2248%22%3E%3Crect fill=%22%23f0f0f0%22 width=%2248%22 height=%2248%22/%3E%3Ctext fill=%22%23999%22 x=%2250%25%22 y=%2250%25%22 text-anchor=%22middle%22 dy=%22.3em%22%3ENo Image%3C/text%3E%3C/svg%3E';

const getOrderImageUrl = item => {
  return item?.image_url || item?.imageUrl || PLACEHOLDER_IMAGE;
};

const handleImageError = event => {
  event.target.src = PLACEHOLDER_IMAGE;
};

const getOrderId = item => item?.order_id || item?.orderId || '';

const handleOrderClick = item => {
  const orderId = getOrderId(item);
  if (orderId) {
    emitter.emit(
      BUS_EVENTS.SEND_MESSAGE,
      t('CONVERSATION.ORDER_CARD.TELL_MORE', { orderId })
    );
  }

  if (item?.action?.type === 'open_url' && item.action.value) {
    window.open(item.action.value, '_blank');
  }
};

const formatDate = dateString => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};
const getItemName = item => item?.item_name || item?.itemName || 'Order item';
const getOrderedOn = item => formatDate(item?.ordered_on || item?.orderedOn);

const getStatusBadgeClass = status => {
  const baseClass = 'oc-status-badge';
  return `${baseClass} oc-status-${status?.toLowerCase() || 'default'}`;
};
</script>

<template>
  <div
    class="text-sm bg-n-solid-blue text-n-slate-12 right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm w-full max-w-full sm:max-w-lg overflow-hidden px-3 py-3"
  >
    <!-- Header -->
    <div v-if="normalizedAttributes.preText" class="pb-4">
      <p
        class="text-[16px] leading-[20px] font-normal text-[#475569] dark:text-white break-words"
      >
        {{ normalizedAttributes.preText }}
      </p>
    </div>

    <!-- Items -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
      <div
        v-for="(item, index) in normalizedAttributes.items"
        :key="getOrderId(item) || `${getItemName(item)}-${index}`"
        class="px-3 py-3 flex gap-3 cursor-pointer hover:bg-n-alpha-1 transition-colors border border-n-strong rounded-lg"
        @click="handleOrderClick(item)"
      >
        <div class="flex-shrink-0">
          <img
            :src="getOrderImageUrl(item)"
            :alt="getItemName(item)"
            class="w-16 h-16 rounded-lg object-contain"
            loading="lazy"
            @error="handleImageError"
          />
        </div>

        <div class="flex-1 min-w-0 flex flex-col gap-1">
          <div class="oc-item-name break-words">
            {{ getItemName(item) }}
          </div>

          <div v-if="getOrderId(item)" class="oc-order-id break-words">
            {{ t('CONVERSATION.ORDER_CARD.ORDER_PREFIX') }}
            {{ getOrderId(item) }}
          </div>

          <div class="flex items-center gap-2 flex-wrap">
            <span
              v-if="getOrderedOn(item)"
              class="oc-order-date text-xs text-n-slate-10"
            >
              {{ getOrderedOn(item) }}
            </span>
            <span v-if="item.status" :class="getStatusBadgeClass(item.status)">
              {{ item.status }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="flex items-end justify-between gap-2 mt-2">
      <span class="text-[10px] leading-[16px] font-normal text-n-slate-11">
        {{ readableTime }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.oc-item-name {
  font-weight: 400;
  font-style: normal;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  text-transform: capitalize;
  color: #475569;
}

.dark .oc-item-name {
  color: white;
}

.oc-order-id {
  font-weight: 700;
  font-style: normal;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  color: #334155;
}

.dark .oc-order-id {
  color: white;
}

.oc-order-date {
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: 0%;
  color: #64748b;
}

.dark .oc-order-date {
  color: white;
}

.oc-status-badge {
  font-size: 0.7rem;
  font-weight: 500;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  text-transform: capitalize;
  white-space: nowrap;
}

.oc-status-badge.oc-status-sale {
  background-color: rgba(34, 197, 94, 0.1);
  color: #059669;
  border-radius: 4px;
  border: 1px solid #059669;
  gap: 10px;
  padding: 2px 8px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: 0%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.oc-status-badge.oc-status-draft {
  background-color: #f1f5f9;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  gap: 10px;
  padding: 2px 8px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: 0%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.oc-status-badge.oc-status-pending {
  background-color: #fefce8;
  color: #ca8a04;
  border: 1px solid #ca8a04;
  border-radius: 4px;
  gap: 10px;
  padding: 2px 8px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: 0%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.oc-status-badge.oc-status-completed {
  background-color: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #1d4ed8;
  border-radius: 4px;
  gap: 10px;
  padding: 2px 8px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: 0%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.oc-status-badge.oc-status-cancelled {
  background-color: #fef2f2;
  color: #dc2626;
  border: 1px solid #dc2626;
  border-radius: 4px;
  gap: 10px;
  padding: 2px 8px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: 0%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.oc-status-badge.oc-status-default {
  background-color: rgba(209, 213, 219, 0.1);
  color: #4b5563;
}
</style>
